import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import "@/js/utils-import";
import "maz-ui/styles";
import "./sass/style.scss";
import "./sass/plugins.scss";
import auth0 from "@/auth";
import VueTippy from "vue-tippy";
import * as Sentry from "@sentry/vue";
import OpenReplay from "@openreplay/tracker";
import { ApplicationInsights, DistributedTracingModes } from "@microsoft/applicationinsights-web";
import posthog from "posthog-js";
import "vue-loaders/dist/vue-loaders.css";
import VueLoaders from "vue-loaders";
//import { VueSpinners } from "@saeris/vue-spinners";
import Popper from "vue3-popper";
import { getWebInstrumentations, initializeFaro } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import trackerAssist from "@openreplay/tracker-assist";
import "vue-skeletor/dist/vue-skeletor.css";
//import ElementPlus from 'element-plus'
import { apiClient } from "@/modules/apiClient";
import VueLogger from "@/plugins/logger/vue-logger";
import { createPinia } from "pinia";
import { LogLevels } from "@/plugins/logger/enum/log-levels";
import { defaultConfig, plugin } from "@formkit/vue";
import "@ionic/vue/css/core.css";
import "vue-toastification/dist/index.css";
import VueCookies from "vue-cookies";
import { IonicVue } from "@ionic/vue";
import capacitorAuth from "@/modules/CapacitorAuth";
import "vue-advanced-cropper/dist/style.css";
import "vue-advanced-cropper/dist/theme.compact.css";
import { VueQueryPlugin } from "@tanstack/vue-query";
// @ts-ignore
//import '@formkit/themes/dist/genesis/theme.css'
// @ts-ignore
import Toast, { PluginOptions } from "vue-toastification";
import "@stripe/stripe-js";

import "@blocknote/mantine/style.css";
import { env } from "@/env";

declare global {
	interface Window {
		initializeKT?: any;
		KTUtil?: any;
		KTMenu?: any;
		KTApp?: any;
		KTLayoutAside?: any;
		KTLayoutExplore?: any;
		KTLayoutSearch?: any;
		KTLayoutToolbar?: any;
		newrelic?: any;
		fcWidget?: any;
		Headway?: any;
		Featurebase?: any;
		fwcrm?: any;
		FM?: any;
		//rnw?: any;
	}
}

export const app = createApp(App);
//.mount("#app");
capacitorAuth();
const isLocalhost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const enableRemoteClientTelemetry = env.ANALYTICS === "enabled" && !isLocalhost;

export const openReplayTracker = new OpenReplay({
	// @ts-ignore
	projectKey: env.OPENREPLAY,
	//ingestPoint: "https://europa12.stagehub.com",
	defaultInputMode: 0,
	obscureInputDates: false,
	obscureInputNumbers: false,
	__DISABLE_SECURE_MODE: isLocalhost,
	revID: env.GITHASH,
	// @ts-ignore
	network: {
		// @ts-ignore
		axiosInstances: [apiClient, appApiClient],
		sanitizer: (data) => {
			const url = new URL(data.url, window.location.origin);

			const allowed = url.hostname === "localhost" || url.hostname.endsWith(".stagehub.com") || url.hostname.endsWith(".stagehub.io");

			return allowed ? data : null;
		},
		capturePayload: true,
		sessionTokenHeader: false,
		ignoreHeaders: ["authorization", "Authorization"],
	},
});

openReplayTracker.use(
	trackerAssist({
		callConfirm: {
			declineBtn: {
				innerHTML: "Decline",
				className: "btn btn-secondary text-white",
			},
			confirmBtn: {
				innerHTML: "Answer",
				className: "btn btn-primary text-white",
			},
		},
	})
);

export const appInsights = new ApplicationInsights({
	config: {
		instrumentationKey: env.APPINSIGHTSKEY,
		autoTrackPageVisitTime: true,
		distributedTracingMode: DistributedTracingModes.AI,
		correlationHeaderDomains: ["*.rest.stagehub.io", "localhost", "*.rest.stagehub.com", "rest.stagehub.com"],
		disableFetchTracking: false,
		enableCorsCorrelation: true,
		enableAjaxPerfTracking: true,
		enableAjaxErrorStatusText: true,
		enableRequestHeaderTracking: true,
		enableResponseHeaderTracking: true,
		excludeRequestFromAutoTrackingPatterns: [/^((?!stagehub).)*$/gm],
		correlationHeaderExcludePatterns: [/^((?!rest.stagehub).)*$/gm],
	},
});

if (env.ANALYTICS === "enabled") {
	segmentAnalytics.load({
		writeKey: env.SEGMENT as string,
	});

	initNewRelic();

	// @ts-ignore
	posthog.init(env.POSTHOG, {
		api_host: "https://europa10.stagehub.com",
		ui_host: "https://us.posthog.com",
		capture_pageview: false,
		enable_recording_console_log: true,
		evaluation_contexts: ["backstage-legacy"],
		logs: {
			serviceName: "app-web",
			environment: env.SH_ENV,
			serviceVersion: env.GITHASH,
		},
		session_recording: {
			blockClass: "record-block",
			blockSelector: "[record-block]",
			ignoreClass: "record-ignore",
			maskAllInputs: false,
			maskInputOptions: {},
			maskInputFn: undefined,
			slimDOMOptions: {},
			collectFonts: true,
			recordBody: true,
		},
	});

	posthog.register({
		platform: "web",
		application: "app-web",
	});

	/* AppInsights */
	appInsights.loadAppInsights();
	appInsights.addTelemetryInitializer((envelope) => {
		if (!envelope.tags) {
			envelope.tags = [];
		}
		envelope.tags.push({ "ai.cloud.role": "app-web" });
	});

	Sentry.init({
		app,
		dsn: "https://5b2a535b508c4d3a9087239380611c4d@o98448.ingest.sentry.io/6450315",
		integrations: [
			Sentry.browserTracingIntegration({
				router,
				enableInp: true,
			}),
			Sentry.replayIntegration({
				mask: ["record-ignore"],
				networkDetailAllowUrls: ["rest"],
				networkCaptureBodies: true,
				maskAllText: false,
				blockAllMedia: false,
			}),
			...(enableRemoteClientTelemetry ? [Sentry.browserProfilingIntegration()] : []),
			// new posthog.SentryIntegration(posthog, "stagehub", 6450315),
		],
		tracePropagationTargets: ["localhost", "prod.rest.stagehub.com", "dev.rest.stagehub.io", "qa.rest.stagehub.io"],
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		environment: env.SH_ENV,
		tracesSampleRate: 1.0,
		profilesSampleRate: enableRemoteClientTelemetry ? 1.0 : 0,
		release: env.GITHASH,
		beforeSend: (event) => {
			if (event.tags) {
				event.tags["OpenReplay"] = openReplayTracker.getSessionURL({ withCurrentTime: true }) || "";
				event.tags["PostHog"] = posthog.get_session_replay_url({ withTimestamp: true }) || "";
			}
			return event;
		},
	});

	openReplayTracker.start().then((tracker) => {
		appInsights.addTelemetryInitializer((envelope) => {
			// Dynamically set the openReplaySessionToken and openReplay URL
			// @ts-ignore
			envelope.data.openReplaySessionToken = openReplayTracker.getSessionID();
			// @ts-ignore
			envelope.data.openReplay = openReplayTracker.getSessionURL({ withCurrentTime: true }) || `https://app.openreplay.com/${env.OPENREPLAY_PROJECTID}/session/${openReplayTracker.sessionID}`;
			// @ts-ignore
			envelope.data.postHog = posthog.get_session_replay_url({ withTimestamp: true });
		});
		if (nrAgent) {
			nrAgent?.setCustomAttribute("OpenReplay", openReplayTracker.getSessionURL({ withCurrentTime: true }) || "");
			nrAgent?.setCustomAttribute("PostHog", posthog.get_session_replay_url({ withTimestamp: true }) || "");
		}
	});

	if (enableRemoteClientTelemetry) {
		initializeFaro({
			url: `https://faro-collector-prod-us-east-2.grafana.net/collect/${env.GRAFANA}`,
			app: {
				name: "app-web",
				version: env.GITHASH || "development",
				environment: env.SH_ENV || "development",
			},

			instrumentations: [...getWebInstrumentations(), new TracingInstrumentation()],
		});
	}
}

VueLogger.install(app, {
	isEnabled: env.SH_ENV !== "Production",
	logLevel: LogLevels.DEBUG,
	separator: "|",
	showConsoleColors: true,
	showLogLevel: false,
	showMethodName: false,
	stringifyArguments: false,
});

import "./modules/FeaturebaseModule";

import Storage from "vue-ls";
import { appApiClient } from "@/modules/appApiClient";
import { segmentAnalytics } from "./modules/webAnalytics";
import { Capacitor } from "@capacitor/core";
import { initNewRelic, nrAgent } from "./modules/NewRelicModule";
import { setVeauryOptions } from "veaury";
import { createRoot } from "react-dom/client";

const LSOptions = {
	namespace: "stagehub__", // key prefix
	name: "ls", // name variable Vue.[ls] or this.[$ls],
	storage: "local", // storage name session, local, memory
};

setVeauryOptions({
	react: {
		createRoot,
	},
});

app
	.component("Popper", Popper)
	//.use(Maska)
	.use(Storage, LSOptions)
	//.use(vfmPlugin)
	.use(VueLoaders)
	//.use(VueSpinners)
	.use(store)
	.use(createPinia())
	//.use(ElementPlus)
	.use(Toast)
	.use(VueCookies)
	.use(IonicVue)
	.use(VueTippy)
	.use(
		plugin,
		defaultConfig({
			config: {
				classes: {
					input: "$reset form-control",
					label: "$reset fs-6 fw-bold form-label mb-2",
				},
			},
		})
	)
	.use(router)
	.use(VueQueryPlugin)
	//ts-ignore
	.use(auth0)
	.mount("#app");

/*const userStore = useUser()
const applicationStore = useApplication()
const vuexPlugin = openReplayTracker.use(trackerVuex())
const piniaUserStorePlugin = vuexPlugin('user')
const piniaApplicationStorePlugin = vuexPlugin('application')
piniaUserStorePlugin(userStore);
piniaApplicationStorePlugin(applicationStore);*/
